<template>
	<v-menu
		v-model="isVisible"
		:activator="targetSelector"
		:close-on-content-click="false"
		:close-on-back="false"
		:location="menuLocation"
		:offset="16"
		persistent
		:scrim="false"
		:z-index="zIndex"
		content-class="tour-tooltip-overlay"
	>
		<v-card
			class="tour-tooltip-card"
			:class="arrowClass"
		>
			<div class="tour-tooltip-title sgnl-text-color-default">
				<span class="sgnl-subtitle-medium">{{ title }}</span>
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="$emit('skip')"
				>
					<Close16 />
				</v-btn>
			</div>
			<div class="tour-tooltip-text sgnl-text-color-secondary sgnl-paragraph-s-regular">
				<slot>{{ text }}</slot>
			</div>
			<TourStepFooter
				:stepNumber="stepNumber"
				:totalSteps="totalSteps"
			>
				<slot name="actions" />
			</TourStepFooter>
		</v-card>
	</v-menu>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import TourStepFooter from "@/components/GuidedTour/TourStepFooter.vue";

export default defineComponent({
	name: "TourTooltipStep",
	components: { Close16, TourStepFooter },
	props: {
		modelValue: { type: Boolean, default: false },
		targetSelector: { type: String, required: true },
		title: { type: String, required: true },
		text: { type: String, default: "" },
		tooltipLocation: { type: String, default: "bottom" },
		zIndex: { type: Number, default: 10000 },
		stepNumber: { type: Number, required: true },
		totalSteps: { type: Number, required: true },
	},
	emits: ["update:modelValue", "skip"],
	data() {
		return {
			detectedArrowClass: "" as string,
			overlayObserver: null as MutationObserver | null,
		};
	},
	computed: {
		isVisible: {
			get(): boolean {
				return this.modelValue;
			},
			set(value: boolean) {
				this.$emit("update:modelValue", value);
			},
		},
		arrowClass(): string {
			return this.detectedArrowClass || this.getArrowClassFromLocation(this.tooltipLocation);
		},
		menuLocation(): "top" | "bottom" | "start" | "end" | "center" {
			const validLocations = ["top", "bottom", "start", "end", "center"] as const;
			return validLocations.find((loc) => loc === this.tooltipLocation) ?? "bottom";
		},
	},
	watch: {
		isVisible(visible: boolean) {
			if (visible) {
				this.scheduleArrowDetection();
			} else {
				this.cleanupOverlayObserver();
			}
		},
		targetSelector() {
			this.scheduleArrowDetection();
		},
	},
	beforeUnmount() {
		this.cleanupOverlayObserver();
	},
	methods: {
		getArrowClassFromLocation(location: string): string {
			const locationMap: Record<string, string> = {
				top: "tour-arrow--bottom",
				bottom: "tour-arrow--top",
				start: "tour-arrow--right",
				end: "tour-arrow--left",
			};
			return locationMap[location.split(" ")[0]] || "tour-arrow--top";
		},
		/**
		 * Schedule arrow detection after Vuetify has positioned the menu overlay.
		 * Uses a short timeout because the v-menu content is teleported to body,
		 * and Vuetify's positioning runs asynchronously after nextTick + rAF.
		 */
		scheduleArrowDetection() {
			this.$nextTick(() => {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						this.detectArrowDirection();
						this.observeOverlayPosition();
					});
				});
			});
		},
		/**
		 * Watch for Vuetify repositioning the overlay (e.g., on scroll or resize).
		 * Vuetify updates inline styles on .v-overlay__content when the menu flips,
		 * so we observe attribute mutations on that container.
		 */
		observeOverlayPosition() {
			this.cleanupOverlayObserver();
			const card = this.findOverlayContent();
			if (!card) return;

			const overlayContent = card.closest(".v-overlay__content");
			if (!overlayContent) return;

			this.overlayObserver = new MutationObserver(() => {
				this.detectArrowDirection();
			});
			this.overlayObserver.observe(overlayContent, {
				attributes: true,
				attributeFilter: ["style"],
			});
		},
		cleanupOverlayObserver() {
			if (this.overlayObserver) {
				this.overlayObserver.disconnect();
				this.overlayObserver = null;
			}
		},
		/**
		 * Find the teleported v-menu overlay content (the .tour-tooltip-card)
		 * by querying the document, since v-menu teleports to body.
		 */
		findOverlayContent(): HTMLElement | null {
			return document.querySelector(".tour-tooltip-card");
		},
		detectArrowDirection() {
			const target = document.querySelector(this.targetSelector);
			const card = this.findOverlayContent();
			if (!target || !card) {
				this.detectedArrowClass = "";
				return;
			}

			const targetRect = target.getBoundingClientRect();
			const cardRect = card.getBoundingClientRect();

			const targetCenterY = targetRect.top + targetRect.height / 2;
			const cardCenterY = cardRect.top + cardRect.height / 2;
			const targetCenterX = targetRect.left + targetRect.width / 2;
			const cardCenterX = cardRect.left + cardRect.width / 2;

			/* Use edge-to-edge gaps instead of center-to-center distances.
			   A narrow target with a wide tooltip card can have a large center
			   dx even when the card is directly below, causing a wrong arrow. */
			const verticalGap = Math.max(0, cardRect.top - targetRect.bottom, targetRect.top - cardRect.bottom);
			const horizontalGap = Math.max(0, cardRect.left - targetRect.right, targetRect.left - cardRect.right);

			if (verticalGap >= horizontalGap) {
				this.detectedArrowClass = cardCenterY > targetCenterY ? "tour-arrow--top" : "tour-arrow--bottom";
			} else {
				this.detectedArrowClass = cardCenterX > targetCenterX ? "tour-arrow--left" : "tour-arrow--right";
			}
		},
	},
});
</script>

<style lang="scss" scoped>
.tour-tooltip-card {
	position: relative;
	overflow: visible;
	max-width: 400px;

	&::before {
		content: "";
		position: absolute;
		width: var(--sgnl-space-12);
		height: var(--sgnl-space-12);
		background: var(--sgnl-default-primary-background);
		border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
		border-left: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
		z-index: 1;
	}

	/* Arrow on top of card (tooltip is below the target) */
	&.tour-arrow--top::before {
		top: calc(var(--sgnl-space-6) * -1);
		left: var(--sgnl-space-24);
		transform: rotate(45deg);
	}

	/* Arrow on bottom of card (tooltip is above the target) */
	&.tour-arrow--bottom::before {
		bottom: calc(var(--sgnl-space-6) * -1);
		left: var(--sgnl-space-24);
		transform: rotate(225deg);
	}

	/* Arrow on left of card (tooltip is to the right of the target) */
	&.tour-arrow--left::before {
		top: var(--sgnl-space-24);
		left: calc(var(--sgnl-space-6) * -1);
		transform: rotate(-45deg);
	}

	/* Arrow on right of card (tooltip is to the left of the target) */
	&.tour-arrow--right::before {
		top: var(--sgnl-space-24);
		right: calc(var(--sgnl-space-6) * -1);
		transform: rotate(135deg);
	}
}

@media (max-width: 959px) {
	.tour-tooltip-card {
		width: 100%;
		max-width: none;
	}
}

.tour-tooltip-title {
	display: flex;
	align-items: center;
	padding: var(--sgnl-space-12) var(--sgnl-space-16);
}

.tour-tooltip-text {
	padding: var(--sgnl-space-12) var(--sgnl-space-16);

	:deep(code) {
		background: var(--sgnl-default-secondary-background);
		padding: var(--sgnl-space-2) var(--sgnl-space-4);
		border-radius: var(--sgnl-border-radius-4);
	}
}
</style>

<style lang="scss">
@media (max-width: 959px) {
	.tour-tooltip-overlay {
		min-width: 95vw !important; // override Vuetify default inline styles
		max-width: 95vw !important; // override Vuetify default inline styles
	}
}
</style>
